// @flow

import * as React from 'react';

import clsx from 'clsx';

import { Base } from '@gandi/ui';

import { AddAllButton } from 'src/Domain/AddAllButton';
import { useAddAllProvider, MIN_ELEMENTS_TO_BULK } from 'src/Domain/modules';
import { ErrorBoundary } from 'src/helpers/ErrorBoundary';

import type { ResultItem } from '../types';

import { DomainRow } from './DomainRow';

import { UnavailableRow } from './UnavailableRow';
import {
  addAllToCart,
  transferPossibility2AvailabilityIconName,
  useSortResultsByPossibility,
} from './modules';

import type { DomainTransferPossibility } from './modules';

import styles from './DomainList.scss';

const exhaustiveSortedGroups: Record<DomainTransferPossibility, void> = {
  transferable: undefined,
  creatable: undefined,
  loading: undefined,
  untransferable: undefined,
  none: undefined,
  error: undefined,
};
const sortedGroups: Array<DomainTransferPossibility> = Object.keys(
  exhaustiveSortedGroups,
);

export function DomainList(): React.MixedElement {
  const results = useSortResultsByPossibility();

  const { loading, handleClick } = useAddAllProvider({
    addAllAction: addAllToCart,
  });

  return (
    <Base marginBottom="base" flex flexCol flexNoWrap className={styles.root}>
      {sortedGroups.map(possibility => {
        const resultGroup = results[possibility];
        const availabilityIconName =
          transferPossibility2AvailabilityIconName[possibility];

        const showBulk =
          possibility === 'transferable' &&
          resultGroup.length >= MIN_ELEMENTS_TO_BULK &&
          resultGroup.find(result => !result.addedToCart);

        if (resultGroup.length === 0) {
          return null;
        }

        return (
          <div
            key={possibility}
            className={clsx(styles.container, styles.showBulk)}
          >
            {showBulk && (
              <AddAllButton loading={loading} onClick={handleClick} />
            )}
            <div className={styles.list}>
              {resultGroup.map((result: ResultItem) => (
                <ErrorBoundary
                  key={result.fqdn}
                  fallback={<UnavailableRow.Error fqdn={result.fqdn} />}
                >
                  {availabilityIconName === 'ok' ? (
                    <DomainRow key={result.fqdn} domain={result} />
                  ) : (
                    <UnavailableRow key={result.fqdn} result={result} />
                  )}
                </ErrorBoundary>
              ))}
            </div>
            {showBulk && (
              <AddAllButton loading={loading} onClick={handleClick} />
            )}
          </div>
        );
      })}
    </Base>
  );
}
